import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Modal from '~/modules/components/global/modal';
import Button from '~/modules/components/global/button';
import Icon from '~/modules/components/global/icon';
import ToastService from '~/modules/services/toast-service';

const RevealBody = styled.div`
	.warning {
		display: flex;
		gap: 0.5rem;
		align-items: flex-start;
		background: ${({ theme }) => theme['midnight-darken-5']};
		border-left: 3px solid ${({ theme }) => theme['warning'] || theme['gold-base']};
		padding: 0.75rem 1rem;
		margin-bottom: 1rem;
		font-size: 0.9rem;
	}

	.token-row {
		display: flex;
		gap: 0.5rem;
		align-items: stretch;
	}

	.token-value {
		flex: 1 1 auto;
		font-family: monospace;
		padding: 0.6rem 0.8rem;
		background: ${({ theme }) => theme['midnight-darken-5']};
		border-radius: 0.25rem;
		word-break: break-all;
		user-select: all;
	}

	.usage-hint {
		margin-top: 1.25rem;
	}

	.usage-hint .label {
		font-size: 0.85rem;
		color: ${({ theme }) => theme['silver-base']};
		margin-bottom: 0.4rem;
	}
`;

const ApiTokenRevealModal = ({
	token,
	onClose,
	title = 'API token created',
	// Defaults describe a personal token. A service account token acts as the account, not as the
	// person who minted it, so callers override this rather than showing misleading copy.
	scopeWarning = 'Anyone with this value can act as you on the API.'
}) => {
	const headerValue = token?.token ? `Bearer ${token.token}` : '';

	return (
		<Modal
			isOpen={!!token}
			contentLabel={title}
			size="lg"
			shouldCloseOnOverlayClick={false}
			onRequestClose={onClose}
		>
			<Modal.Header>
				<h3>{title}</h3>
			</Modal.Header>
			<Modal.Body>
				<RevealBody>
					<div className="warning">
						<Icon use="warning" color="gold-base" />
						<div>
							<strong>Copy this token now.</strong> This is the only time it will be shown.
							{' '}{scopeWarning}
						</div>
					</div>
					<div className="token-row">
						<div className="token-value" data-testid="api-token-revealed-value">
							{token?.token}
						</div>
						<CopyToClipboard
							text={token?.token || ''}
							onCopy={() => ToastService.create('success', 'Token copied to clipboard')}
						>
							<Button variant="secondary" data-testid="api-token-copy-button">
								<Icon use="copy" color="white" /> Copy
							</Button>
						</CopyToClipboard>
					</div>
					<div className="usage-hint">
						<div className="label">Send as an Authorization header:</div>
						<div className="token-row">
							<div className="token-value" data-testid="api-token-header-value">
								Authorization: {headerValue}
							</div>
							<CopyToClipboard
								text={headerValue}
								onCopy={() => ToastService.create('success', 'Header value copied')}
							>
								<Button variant="secondary" data-testid="api-token-header-copy-button">
									<Icon use="copy" color="white" /> Copy
								</Button>
							</CopyToClipboard>
						</div>
					</div>
				</RevealBody>
			</Modal.Body>
			<Modal.Footer>
				<Button onClick={onClose} data-testid="api-token-reveal-close-button">
					Done
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

ApiTokenRevealModal.propTypes = {
	token: PropTypes.shape({
		_id: PropTypes.string,
		name: PropTypes.string,
		token: PropTypes.string
	}),
	onClose: PropTypes.func.isRequired,
	title: PropTypes.string,
	scopeWarning: PropTypes.string
};

export default ApiTokenRevealModal;
